import { toast } from 'react-toastify';
import { httpPost, httpGet, httpDelete } from '../../HttpRequestHandling/httpRequestHandler';
import { Constants } from '../Common/Constants';
import { FormatToInputDate } from '../Common/Utilities';


// =============================================
// Rescan Original Invoice (re-queue for scanning)
// =============================================
// NOTE: Backend reuses existing manual-upload + scan pipeline.
// This UI helper only calls the new API.
export const rescanOriginalInvoice = async ({ modelDefId, companyCode, applyToInvoiceDocumentId }) => {
	const requestBody = {
		modelDefId: Number(modelDefId || 0),
		companyCode: Number(companyCode || 0),
		applyToInvoiceDocumentId: Number(applyToInvoiceDocumentId || 0)
	};

	if (!requestBody.applyToInvoiceDocumentId) {
		throw new Error('applyToInvoiceDocumentId is required');
	}

	// Endpoint name is intentionally explicit. Keep aligned with backend.
	return await httpPost('/api/invoicedocument/rescanoriginalinvoice', requestBody);
};



export const needsRevertTo40 = (invoiceDocument, modelDefId, props) => {
	const details = invoiceDocument.invoiceDocumentEditorDetails || [];

	if (modelDefId === 1) {
		return details.some(d => !d.ioNumber || d.isIONumberValid !== "Y");
	}

	if (modelDefId === 2) {
		return details.some(d =>
			!d.level2Key || d.isLevel2KeyValid !== "Y" ||
			!d.level3Key || d.isLevel3KeyValid !== "Y" ||
			!d.resType || d.isCostCodesValid !== "Y"
		);
	}

	return false;
};
export const canLineRevertTo40 = (line) => {
	const status = Number(line.lineStatus);
	if (status > 70 && status !== 100) return false;

	return true;
};
export const isLineInvalid = (line, modelDefId) => {
	if (!line) return false;

	if (modelDefId === 1) {
		return (
			!line.ioNumber ||
			line.isIONumberValid !== 'Y'
		);
	}

	if (modelDefId === 2) {
		return (
			!line.level2Key ||
			line.isLevel2KeyValid !== 'Y' ||

			!line.level3Key ||
			line.isLevel3KeyValid !== 'Y' ||

			!line.resType ||
			line.isCostCodesValid !== 'Y'
		);
	}

	return false;
};



export const creditMemo = async (props, state, close_clickhandler, reopenCallback, setCreditedMemoInvoiceId) => {
	try {
		props.info.inProgress(true);

		const requestBody = {
			invoiceDocumentId: state.invoiceDocument?.invoiceDocumentId,
			invoiceDocumentFileLocation: state.invoiceDocument?.invoiceDocumentFileLocation,
			invoiceDocumentStatusId: state.invoiceDocument?.invoiceDocumentStatusId,
			modelDefId: state.invoiceDocument?.modelDefId
		};

		const response = await httpPost('/api/invoicedocument/creditmemo', requestBody);

		if (response.status === 200) {
			const invoiceDocument = response.data;
			toast.success("Memo has been credited successfully");

			const memoInvoiceDocumentId =
				invoiceDocument?.invoiceDocumentId ||
				invoiceDocument?.invoiceDocument?.invoiceDocumentId ||
				null;

			// Preferred: open nested credit memo editor (no close+reopen)
			if (memoInvoiceDocumentId && typeof setCreditedMemoInvoiceId === 'function') {
				setCreditedMemoInvoiceId(memoInvoiceDocumentId);
			}
			if (memoInvoiceDocumentId && typeof reopenCallback === 'function') {
				reopenCallback(memoInvoiceDocumentId, invoiceDocument);
				return;
			}

			// Fallback: preserve legacy behavior when nested dialog callbacks are not provided
			close_clickhandler(null, true, false, invoiceDocument);
		} else {
			toast.error("Failed to credit the memo. Please try again later.");
		}
	} catch (error) {
		toast.error(<div>Failed to credit the memo.<br />{error?.response?.data}</div>);
	} finally {
		props.info.inProgress(false);
	}
};


export const accept_clickHandler = async (
	state,
	props,
	getErrorMessage,
	handleClientWarning,
	handleScannedVsTotalWarning,
	IsSaveDisabled,
	editorGridRef,
	broadcastSpotLinesRef,
	attachmentRef,
	httpPost,
	close_clickHandler_wrapper,
	validateInvoiceNumberBeforeSubmit = null) => {
	if (props?.info?.modelDefId === 3) {
		broadcastSpotLinesRef?.current?.revalidateAll?.(state?.invoiceDocument);
	}

	if (typeof validateInvoiceNumberBeforeSubmit === 'function') {
		const canProceed = await validateInvoiceNumberBeforeSubmit();
		if (!canProceed) {
			const validationResponse = getErrorMessage();
			if (validationResponse?.errorList?.length > 0) {
				toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
			}
			return;
		}
	}

	var invoiceDocument = structuredClone(state.invoiceDocument);

	let validationResponse = getErrorMessage();
	if (validationResponse.isStopable) {
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}
	if (props.info.modelDefId === 3) {
		const isClientSelected = invoiceDocument.clientCode && invoiceDocument.clientCode.trim() !== "";
		if (!isClientSelected) {
			if (invoiceDocument.requiresClientCode === 1) {
				try {
					await handleClientWarning();
				} catch {
					return;
				}
			}
		}
	}
	const ok = await confirmScannedVsTotal({ invoiceDocument, state }, handleScannedVsTotalWarning, props);
	if (!ok) return;

	if (IsSaveDisabled()) {
		validationResponse = getErrorMessage();
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}

	try {
		props.info.inProgress(true);

		invoiceDocument.invoiceDocumentEditorDetails = (state?.invoiceDocument?.invoiceDocumentEditorDetails || []);
		invoiceDocument.invoiceDocumentAttachments = (attachmentRef?.current?.attachments || []);
		if (props?.info?.modelDefId === 3) {
			const liveLines = broadcastSpotLinesRef?.current?.lines;
			if (liveLines) invoiceDocument.broadcastSpotLines = liveLines;
		}

		const response = await httpPost("/api/invoicedocument/reviewaccept/", invoiceDocument);

		toast.success('Accepted Successfully');

		const updated = response?.data || invoiceDocument;
		const invoiceDocumentId =
			updated?.invoiceDocumentId ||
			updated?.invoiceDocument?.invoiceDocumentId ||
			invoiceDocument?.invoiceDocumentId ||
			null;

		const statusId =
			updated?.invoiceDocumentStatusId ||
			updated?.invoiceDocument?.invoiceDocumentStatusId ||
			invoiceDocument?.invoiceDocumentStatusId ||
			0;

		// Close like Save: bubble wasUpdated + invoice id so list/report refresh works consistently
		if (typeof close_clickHandler_wrapper === 'function') {
			close_clickHandler_wrapper({
				wasUpdated: true,
				statusId,
				isSaved: true,
				updatedRowFields: invoiceDocumentId ? { invoiceDocumentId } : null,
				invoiceDocumentIdOverride: invoiceDocumentId
			});
		}
	} catch (error) {
		if (error?.response?.status === 401 || error?.response?.status === 403) {
			toast.error(<div>Accept Failed<br />{'- user is unauthorize to perform this action'}</div>);
		} else {
			toast.error(<div>Accept Failed<br />{error?.response?.data}</div>);
		}
	} finally {
		props.info.inProgress(false);
	}
}

export const reject_clickHandler = async (
	state,
	props,
	getErrorMessage,
	handleClientWarning,
	handleScannedVsTotalWarning,
	IsSaveDisabled,
	editorGridRef,
	broadcastSpotLinesRef,
	attachmentRef,
	httpPost,
	close_clickHandler_wrapper,
	validateInvoiceNumberBeforeSubmit = null) => {
	if (props?.info?.modelDefId === 3) {
		broadcastSpotLinesRef?.current?.revalidateAll?.(state?.invoiceDocument);
	}

	if (typeof validateInvoiceNumberBeforeSubmit === 'function') {
		const canProceed = await validateInvoiceNumberBeforeSubmit();
		if (!canProceed) {
			const validationResponse = getErrorMessage();
			if (validationResponse?.errorList?.length > 0) {
				toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
			}
			return;
		}
	}

	var invoiceDocument = structuredClone(state.invoiceDocument);
	let validationResponse = getErrorMessage();
	const statusBeforeReject = state.originalInvoiceDocumentStatus;

	if (validationResponse.isStopable) {
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}
	if (props.info.modelDefId === 3) {
		const isClientSelected = invoiceDocument.clientCode && invoiceDocument.clientCode.trim() !== "";
		if (!isClientSelected) {
			if (invoiceDocument.requiresClientCode === 1) {
				try {
					await handleClientWarning();
				} catch {
					return;
				}
			}
		}
	}
	const ok = await confirmScannedVsTotal({ invoiceDocument, state }, handleScannedVsTotalWarning, props);
	if (!ok) return;

	if (IsSaveDisabled()) {
		validationResponse = getErrorMessage();
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return;
	}

	try {
		props.info.inProgress(true);

		invoiceDocument.invoiceDocumentEditorDetails = (state?.invoiceDocument?.invoiceDocumentEditorDetails || []);
		invoiceDocument.invoiceDocumentAttachments = (attachmentRef?.current?.attachments || []);
		if (props?.info?.modelDefId === 3) {
			const liveLines = broadcastSpotLinesRef?.current?.lines;
			if (liveLines) invoiceDocument.broadcastSpotLines = liveLines;
		}

		const response = await httpPost("/api/invoicedocument/reviewreject/", invoiceDocument);

		toast.success('Rejected Successfully');

		const updated = response?.data || invoiceDocument;
		const invoiceDocumentId =
			updated?.invoiceDocumentId ||
			updated?.invoiceDocument?.invoiceDocumentId ||
			invoiceDocument?.invoiceDocumentId ||
			null;

		const statusId =
			updated?.invoiceDocumentStatusId ||
			updated?.invoiceDocument?.invoiceDocumentStatusId ||
			invoiceDocument?.invoiceDocumentStatusId ||
			0;
		const isInvoiceStatusChanged = statusBeforeReject !== statusId;


		// Close like Save: bubble wasUpdated + invoice id so list/report refresh works consistently
		if (typeof close_clickHandler_wrapper === 'function') {
			close_clickHandler_wrapper({
				wasUpdated: true,
				statusId,
				isSaved: true,
				updatedRowFields: invoiceDocumentId
					? { invoiceDocumentId }
					: null,
				invoiceDocumentIdOverride: invoiceDocumentId
			});
		}
	} catch (error) {
		if (error?.response?.status === 401 || error?.response?.status === 403) {
			toast.error(<div>Reject Failed<br />{'- user is unauthorize to perform this action'}</div>);
		} else {
			toast.error('Reject Failed');
		}
	} finally {
		props.info.inProgress(false);
	}
}

export const validateInsertionLevelMatching = (invoiceDocument, state, toast) => {
	const details = invoiceDocument.invoiceDocumentEditorDetails || [];
	if (details.length === 0) return true;

	// 1. Check for mixed modes
	const modes = new Set();
	for (const d of details) {
		if (d.ioSessionMode) {
			modes.add(d.ioSessionMode);
		}
	}

	if (modes.size > 1) {
		toast.error("Cannot save invoice: All lines must use the same matching mode. Please select either 'Invoice by IO' or 'Invoice by Placement' consistently across all lines.");
		return false;
	}

	const sessionMode = modes.size === 1 ? Array.from(modes)[0] : (state?.ioSessionMode || state?.invoiceDocument?.ioSessionMode || 'io');

	// 2. Validate Placement allocations sum > 0
	if (sessionMode === 'placement') {
		let totalAllocation = 0;
		for (const detail of details) {
			if (detail?.placements && Array.isArray(detail.placements)) {
				totalAllocation += detail.placements.reduce((sum, p) => sum + Number(p?.allocAmount ?? p?.netAmount ?? 0), 0);
			} else {
				totalAllocation += Number(detail?.allocatedAmount ?? detail?.netAmount ?? 0);
			}
		}

		if (totalAllocation <= 0) {
			toast.error("Cannot save invoice: Total placement allocation must be greater than zero.");
			return false;
		}
	}

	// 3. IO Tolerance Validation (applies to BOTH modes since placement aggregate should equal IO limit)
	const groupedByIO = {};
	for (const detail of details) {
		if (!detail?.ioNumber) continue;
		const key = detail.ioNumber;
		if (!groupedByIO[key]) {
			groupedByIO[key] = {
				ioNumber: detail.ioNumber,
				poAmount: Number(detail.poAmount || 0),
				consumedAmount: Number(detail.consumedAmount || 0),
				tolerancePOFlag: detail.tolerancePOFlag,
				tolerancePOAmount: Number(detail.tolerancePOAmount || 0),
				ioInvoiceAmount: 0
			};
		}

		groupedByIO[key].ioInvoiceAmount += Number(detail?.allocatedAmount ?? detail?.ioInvoiceAmount ?? 0);
	}

	for (const d of Object.values(groupedByIO)) {
		let remainingAmount = d.poAmount - d.consumedAmount;
		let remainingWithTol = remainingAmount;

		if (d.tolerancePOFlag === 'N') {
			remainingWithTol += d.tolerancePOAmount;
		} else {
			remainingWithTol += (d.tolerancePOAmount * d.poAmount) / 100.00;
		}

		if (remainingWithTol < 0) remainingWithTol = 0;

		const isInvalid = Number(d.ioInvoiceAmount.toFixed(2)) > Number(remainingWithTol.toFixed(2));

		if (isInvalid) {
			toast.error(`Cannot save invoice: IO ${d.ioNumber} exceeds maximum budget (including tolerance).`);
			return false;
		}
	}

	return true;
};



export const save_clickHandler = async (
state,
setState = null,
props,
editorGridRef,
broadcastSpotLinesRef,
attachmentRef,
isTaxDiscrepent,
IsSaveDisabled,
InvoiceDocumentDetailMissingInfo,
allowDiscrepentTax,
getErrorMessage,
handleClientWarning,
handleScannedVsTotalWarning,
isSaveBtnDisabled,
overrideAllChks,
handleOverrideWarning,
httpPost,
httpDelete,
fetchInvoiceDocument,
close_clickHandler,
Constants,
toast,
navigateAfterSave = true,
setSuccessfullSave,
getInvoiceEditorCapabilities,
validateInvoiceNumberBeforeSubmit = null) => {
if (typeof validateInvoiceNumberBeforeSubmit === 'function') {
	const canProceed = await validateInvoiceNumberBeforeSubmit();
	if (!canProceed) {
		const validationResponse = getErrorMessage();
		if (validationResponse?.errorList?.length > 0) {
			toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		}
		return false;
	}
}

if (props?.info?.modelDefId === 3) {
	broadcastSpotLinesRef?.current?.revalidateAll?.(state?.invoiceDocument);
}

	var invoiceDocument = structuredClone(state.invoiceDocument);
	const statusBeforSave = state.originalInvoiceDocumentStatus;
	invoiceDocument.invoiceDocumentEditorDetails = (state?.invoiceDocument?.invoiceDocumentEditorDetails || []);
	if (props?.info?.modelDefId === 3) {
		invoiceDocument.serviceTermStartDate = invoiceDocument?.serviceTermStartDateString || '';
		invoiceDocument.serviceTermEndDate = invoiceDocument?.serviceTermEndDateString || '';
	}
	let _isTaxDiscrepent = isTaxDiscrepent(invoiceDocument.invoiceDocumentEditorDetails);
	invoiceDocument.invoiceDocumentAttachments = (attachmentRef?.current?.attachments || []);
	if (props?.info?.modelDefId === 3) {
		const liveLines = broadcastSpotLinesRef?.current?.lines;
		if (liveLines) invoiceDocument.broadcastSpotLines = liveLines;
	}

	const invalidRows = getInvalidIoPoRowsForServiceTerm(invoiceDocument);

	if (invalidRows.length > 0) {

		toast.error(
			<div>
				{["The following IO/PO(s) have service dates outside the invoice service date range:"]
					.concat(
						invalidRows.map((line, idx) =>
							`- IO/PO ${line.ioNumber} (line #${idx + 1})`
						)
					)
					.map((msg, i) => <div key={i}>{msg}</div>)}
			</div>
		);

		return false;
	}

	if (invoiceDocument?.modelDefId === 1 && invoiceDocument?.insertionLevelMatchingCriteria === 1) {
		if (!validateInsertionLevelMatching(invoiceDocument, state, toast)) {
			return false;
		}
	}

	if (IsSaveDisabled() || InvoiceDocumentDetailMissingInfo() || (!allowDiscrepentTax && _isTaxDiscrepent)) {
		if (state.mode === "ManualEntry" && invoiceDocument.invoiceDocumentStatusId === 50 && invoiceDocument.invoiceDocumentStatusAttribute == "M") {
			invoiceDocument.invoiceDocumentStatusId = 40;
		} else if (invoiceDocument.invoiceDocumentStatusId === 50 && invoiceDocument.invoiceDocumentStatusAttribute == "C") {
			invoiceDocument.invoiceDocumentStatusId = 40;
			invoiceDocument.invoiceDocumentEditorDetails.forEach(detail => {
				if (isLineInvalid(detail, invoiceDocument.modelDefId) || parseFloat(detail.ioInvoiceAmount || 0) === 0) {
					detail.lineStatus = 40;
				}
			});
		}
	}
	let validationResponse = getErrorMessage();

	if (validationResponse.isStopable) {
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return false;
	}

	if (props.info.modelDefId === 3) {
		const isClientSelected = invoiceDocument.clientCode && invoiceDocument.clientCode.trim() !== "";
		if (!isClientSelected) {
			if (invoiceDocument.requiresClientCode === 1) {
				try {
					await handleClientWarning();
				} catch {
					return false;
				}
			}
		}
	}

	const chkAllValid =
		state.mode !== "MakeCorrections" ||
		(state.originalInvoiceDocumentStatus !== 60 && state.originalInvoiceDocumentStatus > 41);


	const mustRevertTo40 =
		(state?.invoiceDocument?.invoiceDocumentId || 0) !== 0 &&
		needsRevertTo40(invoiceDocument, props.info.modelDefId, props);

	let _isSaveDisabled;

	if (mustRevertTo40) {
		_isSaveDisabled = false;
	}
	else if (!chkAllValid) {
		_isSaveDisabled = isSaveBtnDisabled;
	}
	else {
		_isSaveDisabled = IsSaveDisabled();
	}

	if (!_isSaveDisabled) {

		if (props.info.modelDefId !== 3) {
			invoiceDocument.invoiceDocumentEditorDetails.forEach(detail => {
				if (detail.StatusDescription === "Approved") {
					detail.StatusDescription = "Approved";
				} else if (detail.StatusDescription === "Rejected") {
					detail.StatusDescription = "Rejected";
				}
			});
		}

		if ((invoiceDocument.invoiceDocumentStatusId >= 50 && invoiceDocument.invoiceDocumentStatusAttribute == "C")
			|| state.mode === "ManualEntry" && invoiceDocument.invoiceDocumentStatusId === 50 && invoiceDocument.invoiceDocumentStatusAttribute == "M"
		) {
			invoiceDocument.invoiceDocumentEditorDetails.forEach(detail => {
				if (detail.lineStatus < 50) {
					detail.lineStatus = 50;
				}
			});
		}
		const isApprovalFirstLevel =
			(invoiceDocument.invoiceDocumentStatusId === 50 || invoiceDocument.invoiceDocumentStatusId === 100);

		//if (isApprovalFirstLevel) {
		const mustRevert = needsRevertTo40(invoiceDocument, props.info.modelDefId, props);

		if (mustRevert) {
			invoiceDocument.invoiceDocumentStatusId = 40;

			invoiceDocument.invoiceDocumentEditorDetails.forEach(line => {
				if (
					//canLineRevertTo40(line) &&
					isLineInvalid(line, invoiceDocument.modelDefId)
				) {
					line.lineStatus = 40;
				}
			});

			// Fix: setState called once outside the loop, and originalInvoiceDocumentStatus
			// is kept in sync so the second save correctly identifies the pre-save status.
			setState(prev => ({
				...prev,
				originalInvoiceStatusIdForGadgetLoading: 50,
				originalInvoiceDocumentStatus: 40
			}));
		}
		//}

		if (!_isTaxDiscrepent) {
			invoiceDocument.taxDiscrepancyFlag = 0;
		}

		const ok = await confirmScannedVsTotal({ invoiceDocument, state }, handleScannedVsTotalWarning, props);
		if (!ok) return false;

		if (state.mode === Constants.OVERRIDE && overrideAllChks) {
			try {
				await handleOverrideWarning()
			}
			catch {
				return;
			}

			invoiceDocument.isModifiedAfterApproval = 'Y';
			invoiceDocument.flowState = 4;
		}
		props.info.inProgress(true);
		try {
			const response = await httpPost(
				"/api/invoicedocument/invoicedocumenteditor/",
				invoiceDocument
			);

			if (typeof response?.data?.records === 'string') {
				let message = response?.data?.records;
				if (message.indexOf('error') === 0) {
					message = message.replace('error: ', '');

					const partialKeywords = ['start date', 'end date', 'run date', 'calendar'];

					const containsKeyword = partialKeywords.some(keyword =>
						message.toLowerCase().includes(keyword)
					);

					if (containsKeyword) {
						toast.error(<div>{message}</div>);
					} else if (message.indexOf('Invoice acceptance reverted') === 0) {
						toast.error(<div>{message}</div>);
					} else {
						// Fix: show the actual server message so the user sees a meaningful error
						// instead of a blank toast. Fall back to a generic label only when empty.
						const displayMessage = message?.trim() || 'An unexpected error occurred. Please try again.';
						toast.error(<div>Update Failed<br />{displayMessage}</div>);
					}
					props.info.inProgress(false);
					return false;

				}

			} else {

				// Fire delete for broadcast spot lines that were removed before saving
				if (props.info.modelDefId === 3) {
					const deletedIds = broadcastSpotLinesRef?.current?.deletedSequenceIds || [];
					if (deletedIds.length > 0) {
						const invoiceDocumentId = invoiceDocument?.invoiceDocumentId;
						const sequenceIds = deletedIds.join(',');
						try {
							await httpDelete(
								`/api/invoicedocument/broadcastspotlines?sequenceIds=${encodeURIComponent(sequenceIds)}&invoiceDocumentId=${invoiceDocumentId}`
							);
						} catch (e) {
							console.error('Failed to delete broadcast spot lines:', e);
						}
					}
				}

				toast.success('Updated Successfully');
				setSuccessfullSave(true);
				if (navigateAfterSave) {

					const updatedInvoice = response.data?.records;
					let savedInvoiceDocumentId = updatedInvoice?.invoiceDocumentId;

					// For NEW invoices, the backend may not immediately return an id (or it may take a moment
					// before it becomes queryable). Try to resolve it by file location with retries.
					if (!savedInvoiceDocumentId) {
						const fileKey = invoiceDocument?.invoiceDocumentFileLocation;
						const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
						if (fileKey) {
							const attempts = 3;
							const delayMs = 2000;
							for (let i = 0; i < attempts; i++) {
								try {
									const byFileRes = await httpGet(
										`/api/invoicedocument/invoicedocumenteditorbyfilename?invoiceDocumentFileLocation=${encodeURIComponent(fileKey)}`
									);
									savedInvoiceDocumentId = byFileRes?.data?.invoiceDocumentId;
									if (savedInvoiceDocumentId) break;
								} catch (e) {
									// swallow and retry
								}
								if (i < attempts - 1) {
									await delay(delayMs);
								}
							}
						}
					}

					if (savedInvoiceDocumentId && updatedInvoice) {
						updatedInvoice.invoiceDocumentId = savedInvoiceDocumentId;
					}
					const hasAuthority = updatedInvoice?.invoiceDocumentEditorDetails?.some(e => e.hasAuthority === "Y") || false;
					const statusId = updatedInvoice?.invoiceDocumentStatusId;
					const approvalLevels = updatedInvoice?.companyApprovalConfiguration?.routingNumberOfApprovalLevels || 0;
					const approvedStatus = (approvalLevels * 20) + 50;
					const wasSavedSuccessfully = true;
					const isInvoiceStatusChanged = (statusBeforSave) != statusId;

					const shouldKeepOpen =
						statusId >= 40 &&
						statusId < 70 &&
						statusId !== 60 &&
						hasAuthority && isInvoiceStatusChanged;


					const totalAmount = updatedInvoice?.invoiceDocumentEditorDetails?.reduce((sum, line) => {
						const net = Number(line?.ioInvoiceAmount || 0);
						const tax = Number(line?.taxAmount || 0);
						return sum + net + tax;
					}, 0);

					const vendor = updatedInvoice.modelDefId === 3
						? `${updatedInvoice.callLetters || ''}` +
						`${updatedInvoice.callLetters && updatedInvoice.band ? '-' : ''}` +
						`${updatedInvoice.band || ''}` +
						`${(updatedInvoice.callLetters || updatedInvoice.band) && updatedInvoice.vendor ? ': ' : ''}` +
						`${updatedInvoice.vendor || ''}`
						: updatedInvoice.vendor;

					const updatedRowFields = {
						hasAuthority: hasAuthority,
						invoiceDocumentId: savedInvoiceDocumentId || updatedInvoice.invoiceDocumentId,
						status: approvedStatus === statusId ? "Ready to Post" : updatedInvoice.status,
						invoiceNumber: updatedInvoice.invoiceNumber,
						totalAmount: totalAmount.toFixed(2),
						dueDate: updatedInvoice.dueDate,
						...(updatedInvoice.modelDefId === 3
							? { payTo: vendor }
							: { vendor })
					};
					const newCaps = getInvoiceEditorCapabilities(updatedInvoice);

					setState(prev => ({
						...prev,
						invoiceDocument: {
							...prev.invoiceDocument,
							...updatedInvoice,
							invoiceDocumentStatusId: statusId === 40 ? 50 : statusId
						},
						invoiceDocumentImage: structuredClone({
							...prev.invoiceDocument,
							...updatedInvoice,
							invoiceDocumentStatusId: statusId
						}),
						capabilities: newCaps.capabilities,
						originalInvoiceDocumentStatus: statusId,
						originalInvoiceStatusIdForGadgetLoading: statusId,
						mode: statusId === 40 ? Constants.MAKE_CORRECTIONS : prev.mode,
						isDirty: false,
						updatedRowFields: updatedRowFields
					}));

					// If we couldn't resolve an invoiceDocumentId for a create, do NOT close.
					if (!updatedRowFields.invoiceDocumentId) {
						toast.warning('Saved successfully, but invoice id was not returned yet. Please wait a moment and click Save again.');
						return true;
					}

					if (!shouldKeepOpen) {
						close_clickHandler(
							null,
							false,
							true,
							null,
							statusId,
							wasSavedSuccessfully,
							updatedRowFields,
							false,
							null,
							updatedRowFields.invoiceDocumentId
						);
					}
				}

			}

			props.info.inProgress(false);
			return true;

		}

		catch (error) {
			props.info.inProgress(false);
			//Invoice acceptance reverted due to no resource available for approval of next level.
			toast.error(<div>Update Failed<br />{error?.response?.data?.detail}</div>);
			return false;

		}
	}
	else {
		if (validationResponse.errorList.length == 0 && !invoiceDocument?.invoiceDocumentFileLocation) {
			validationResponse.errorList = ['The invoice encountered the following errors', '- Invoice document is missing'];
		}
		toast.error(<div>{validationResponse.errorList.map(e => <div>{e}</div>)}</div>, { allowHtml: true });
		return false;
	}

}

export const savePostedInvoiceComments = async (
	state,
	props,
	httpPost,
	toast,
	setSuccessfullSave,
	close_clickHandler
) => {
	const invoiceDocument = state?.invoiceDocument;

	if (!invoiceDocument?.invoiceDocumentId) {
		toast.error("Invalid invoice document");
		return false;
	}

	try {
		props.info.inProgress(true);

		await httpPost(
			"/api/invoicedocument/savepostedcomments",
			invoiceDocument
		);

		toast.success("Comments updated successfully");
		setSuccessfullSave(true);

		// 🔥 ONLY reload gadget 4 row
		close_clickHandler(
			null,
			false,
			true,
			{ reloadMode: "POSTED_COMMENT_ONLY" },
			invoiceDocument.invoiceDocumentStatusId,
			true,
			null,
			false,
			null,
			invoiceDocument.invoiceDocumentId
		);

		return true;

	} catch (error) {
		console.error("Save posted comments error:", error);
		toast.error("Failed to update comments");
		return false;
	} finally {
		props.info.inProgress(false);
	}
};


export const routeToEmployee_clickHandler = async (
	state,
	setState,
	handleRouteToEmployeeWarning,
	save_clickHandler) => {
	let isSaveConfirmed = false;

	if (state.isDirty) {
		try {
			isSaveConfirmed = await handleRouteToEmployeeWarning();
			if (isSaveConfirmed) {
				const saveSuccessful = await save_clickHandler();
				if (!saveSuccessful) {
					return;
				}
			}
		} catch (error) {
			return;
		}
	}

	const isProduction = state.invoiceDocument?.modelDefId === 2;
	setState((prev) => ({
		...prev,
		openPOHeaderResourceDialog: !isProduction,
		openLevel2ResourceDialog: isProduction,
		isDirty: isSaveConfirmed ? false : prev.isDirty,
	}));
};

export const confirmScannedVsTotal = async ({ invoiceDocument = null, state = null }, handleScannedVsTotalWarning, props, tolerance = 0.01) => {
	const scannedTotal = Number(invoiceDocument?.scannedTotal ?? 0);
	const { mode } = state;
	const details = invoiceDocument?.invoiceDocumentEditorDetails || [];
	const calculatedTotal = details.reduce(
		(sum, d) =>
			sum +
			Number(d?.ioInvoiceAmount ?? d?.invoiceAmount ?? 0) +
			Number(d?.taxAmount ?? 0),
		0
	);

	if (!Number.isNaN(scannedTotal)) {
		const diff = Math.abs(scannedTotal - calculatedTotal);
		if (diff < tolerance) {
			invoiceDocument.userAcceptDiscrepancy = '';
			return true;
		}
	}

	if (invoiceDocument?.userAcceptDiscrepancy && invoiceDocument.userAcceptDiscrepancy.trim() !== '') {
		return true;
	}
	if (mode === 'UserEntry' || invoiceDocument?.invoiceDocumentFileLocation?.toUpperCase()?.includes('USER_ENTRY_') || mode == Constants.CREDIT_MEMO) {
		return true;
	}

	if (!Number.isNaN(scannedTotal)) {
		const diff = Math.abs(scannedTotal - calculatedTotal);
		if (diff >= tolerance) {
			// Format both totals with commas and two decimal places
			const formattedScanned = scannedTotal.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			const formattedCalculated = calculatedTotal.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});

			const msg = `The scanned total amount (${formattedScanned}) doesn't match the invoice total amount (${formattedCalculated}). Do you want to continue?`;
			try {
				await handleScannedVsTotalWarning(msg);
				const resourceId = (JSON.parse(localStorage.getItem('userInfo') || '{}')?.id ?? 'System');
				invoiceDocument.userAcceptDiscrepancy =
					(invoiceDocument?.userAcceptDiscrepancy ?? '').toString().trim() !== ''
						? invoiceDocument.userAcceptDiscrepancy
						: resourceId;
			} catch {
				return false;
			}
		}
	}
	return true;
};



export const handleRouteToEmployeeWarning = (
	setOpenRouteToEmployeeDialog,
	setDialogResponseHandler) => {


	return new Promise((resolve) => {
		setOpenRouteToEmployeeDialog(true);

		const handleResponse = (event) => {
			setOpenRouteToEmployeeDialog(false);
			if (event.response === 'Y') {
				resolve(true);
			} else {
				resolve(false);
			}

		};
		setDialogResponseHandler(() => handleResponse);

	})
}

export const handleClientWarning = (setOpenClientWarningDialog,
	setDialogResponseHandler) => {
	return new Promise((resolve, reject) => {
		setOpenClientWarningDialog(true);

		const handleResponse = (event) => {
			setOpenClientWarningDialog(false);
			if (event.response === 'Y') {
				resolve();
			} else {
				reject();
			}
		};

		setDialogResponseHandler(() => handleResponse);
	});
};
export const handleOverrideWarning = (
	setConfirmationDialogOpen,
	setOverrideDialogResponseHandler
) => {
	return new Promise((resolve, reject) => {
		setConfirmationDialogOpen(true);

		const handleResponse = (event) => {
			setConfirmationDialogOpen(false);
			if (event.response === 'Y') {
				resolve();
			} else {
				reject();
			}
		};

		setOverrideDialogResponseHandler(() => handleResponse);
	});
};

export const handleCustomWarning = (
	setDialogOpen,
	setDialogResponseHandler,
	setDialogMessage,
	message
) => {
	return new Promise((resolve, reject) => {
		setDialogMessage(message);
		setDialogOpen(true);

		const handleResponse = (event) => {
			setDialogOpen(false);
			if (event.response === 'Y') resolve();
			else reject();
		};

		setDialogResponseHandler(() => handleResponse);
	});
};


//export const routingHistory_clickHandler = (setState, invoiceDocumentId, seqId) => {
//	setState((prev) => ({
//		...prev,
//		openInvoiceDocumentRoutingHistoryDialog: true,
//		routingHistoryData: { invoiceDocumentId, seqId, historyTitle: "Line Level Approval History" }
//	}));
//}
export const invoiceDocumentRoutingHistoryDialog_closeHandler = (setState) => {
	setState((prev) => ({ ...prev, openInvoiceDocumentRoutingHistoryDialog: false }));
}

export const input_changeHandler = (event, state, setState, setDateValidations) => {
	var invoiceDocument = state.invoiceDocument;
	var targetName = event.currentTarget.name;
	invoiceDocument[event.currentTarget.name] = event.currentTarget.value;
	if (event.currentTarget.dataset && event.currentTarget.dataset.name) {
		invoiceDocument[event.currentTarget.dataset.name] = event.currentTarget.value;
	}

	//Reset if currency not matching
	if (targetName == "currencyCode" && event.currentTarget.value != invoiceDocument.currencyCode) {
		invoiceDocument.vendorCode = "";
		invoiceDocument.shortName = "";
		invoiceDocument.vendor = "";
		invoiceDocument.siteId = "";
		//invoiceDocument.currencyCode = "";
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, isDirty: false }));
	}
	else if (targetName === 'invoiceDateString') {
		let nglDate = state.invoiceDocument?.glDateString;
		if (!nglDate) {
			nglDate = event.currentTarget.value;
		}
		state.invoiceDocument.glDate = nglDate;
		state.invoiceDocument.glDateString = nglDate;
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, isDirty: true }));
	}
	else
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, isDirty: true }));
	setDateValidations(invoiceDocument);
}

export const onPaymentTermsChange = (
	event,
	state,
	setState,
	setDateValidations,
	input_changeHandler,
	addDaysInDate) => {
	input_changeHandler(event, state, setState, setDateValidations);
	var paymentTerm = state.invoiceDocument?.paymentTerms?.find(val => val.termsCode === state.invoiceDocument?.paymentTermCode);
	if (!paymentTerm) return;
	var daysDue = parseInt(paymentTerm.daysDue, 10);
	var invoiceDate = new Date(state.invoiceDocument?.invoiceDateString);
	var dueDate = addDaysInDate(invoiceDate, daysDue);
	var isValidDueDate = ((dueDate).toString()).indexOf('Invalid Date') === -1;
	var invoiceDocument = state.invoiceDocument;
	invoiceDocument.dueDate = dueDate;
	if (dueDate && isValidDueDate && dueDate?.toISOString().length > 10)
		invoiceDocument.dueDateString = dueDate.toISOString().slice(0, 10);
	setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument }));
	setDateValidations(invoiceDocument);
}

export const serviceTerm_blurHandler = (
	event,
	props,
	state,
	setState,
	currentEditRow,
	toast,
	stringIsNullOrEmpty,
	uniqueBy,
	editorGridRef) => {
	if (props.info.modelDefId !== 3) return;
	var pocodes = [];
	if (state.invoiceDocument?.invoiceDocumentEditorDetails.length > 0) {
		var invoiceDocument = state.invoiceDocument;
		var serviceTermStartDate = invoiceDocument.serviceTermStartDate;
		var serviceTermEndDate = invoiceDocument.serviceTermEndDate;
		if (stringIsNullOrEmpty(state.invoiceDocument?.serviceTermStartDate) || stringIsNullOrEmpty(state.invoiceDocument?.serviceTermEndDate)) {
			state.invoiceDocument?.invoiceDocumentEditorDetails.map(function (detail, index) {
				detail.deliveryAmount = 0.00;
			});
			if (currentEditRow) {
				currentEditRow.deliveryAmount = 0.00;
			}
		}
		else if (state.serviceTermStartDate !== state.invoiceDocument?.serviceTermStartDate || state.serviceTermEndDate !== state.invoiceDocument?.serviceTermEndDate) {
			if ((state.serviceTermStartDate?.length || 0) > 10) {
				return;
			}
			var uniqueInvoiceDocumentEditorDetails = uniqueBy(state.invoiceDocument?.invoiceDocumentEditorDetails, ["ioNumber"]);
			uniqueInvoiceDocumentEditorDetails.map(function (detail, index) {
				if (!stringIsNullOrEmpty(detail.ioNumber)) pocodes.push(detail.ioNumber.trim());
			});
			var params = {
				pocodes: pocodes,
				serviceTermStartDate: stringIsNullOrEmpty(state.invoiceDocument?.serviceTermStartDate) ? null : state.invoiceDocument?.serviceTermStartDate,
				serviceTermEndDate: stringIsNullOrEmpty(state.invoiceDocument?.serviceTermEndDate) ? null : state.invoiceDocument?.serviceTermEndDate
			}
			props.info.inProgress(true);
			httpPost("/api/lookup/poheaderlistbypocodelist/", params)
				.then((response) => {
					var poHeaders = response.data;
					editorGridRef.current.setDetailsOnserviceTermBlur(poHeaders)
					if (currentEditRow) {
						currentEditRow.deliveryAmount = poHeaders.find(poHeader => poHeader.poCode.trim() === currentEditRow.ioNumber.trim()).deliveryAmount;
					}
					props.info.inProgress(false);
				}).catch((error) => {
					props.info.inProgress(false);
					if (error.response && error.response.data) {
						let errorsList = [];
						let errors = error.response.data?.errors;
						if (errors.response.data?.title) {
							errorsList = [errors.response.data?.title]
						}
						toast.error(<div>Delivery Amount update failed<br />{errorsList.map((e, i) => (<div>{e}</div>))}</div>);
					}
				});
		}
		setState((prev) => ({ ...prev, invoiceDocument: invoiceDocument, serviceTermStartDate: serviceTermStartDate, serviceTermEndDate: serviceTermEndDate }));
	}
}
export const vendorDialog_closeHandler = (
	event,
	vendor,
	state,
	setState,
	stringIsNullOrEmpty,
	editorGridRef,
	setVendor) => {
	if (vendor && !stringIsNullOrEmpty(state.invoiceDocument?.vendorCode) && state.invoiceDocument?.vendorCode !== vendor.vendorCode &&
		((state.invoiceDocument?.modelDefId === 1 && state?.invoiceDocument?.invoiceDocumentEditorDetails.length > 0) ||
			(state.invoiceDocument?.modelDefId === 2 && !stringIsNullOrEmpty(state.invoiceDocument?.poCode)))
	) {
		setState((prev) => ({ ...prev, openVendorDialog: false, vendor: vendor, openChangeVendorConfirmation: true }));
	}
	else {
		setState((prev) => ({ ...prev, openVendorDialog: false, poHeaderLookup: [], isDirty: true }));
		setVendor(vendor);
	}
}
export const clientDialog_closeHandler = (
	event,
	selectedClient,
	setState) => {
	const newClientCode = selectedClient ? selectedClient.clientCode : '';
	const newClientName = selectedClient ? selectedClient.clientName : '';
	setState((prev) => {
		return {
			...prev,
			openClientDialog: false,
			isDirty: true,
			invoiceDocument: {
				...prev.invoiceDocument,
				clientCode: newClientCode,
				clientName: newClientName,
				client: newClientCode && newClientName ? `${newClientCode} - ${newClientName}` : '',
			},
			previousClientData: selectedClient || null,
			poHeaderLookup: []
		};
	});
};
export const search_clickHandler = (event, setState) => {
	setState((prev) => ({ ...prev, openVendorDialog: true }));
}
export const searchClient_clickHandler = (event, setState) => {
	setState((prev) => ({ ...prev, openClientDialog: true, fetchClientData: true }));
}
export const handleTabInvoiceDetails = (setActiveTab) => {
	setActiveTab("tabInvoiceDetails");
};

export const handleTabAttachments = (setActiveTab) => {
	setActiveTab("tabAttachments");
};


export const VIEW_ATTRIBUTE = {
	READ_ONLY: 'READ_ONLY',
	EDITABLE: 'EDITABLE',
	APPROVAL: 'APPROVAL'
};

export const STATUS_ATTRIBUTE = {
	NORMAL: 'NORMAL',
	CORRECTION: 'CORRECTION',
	MANUAL: 'MANUAL'
};




export const INVOICE_EDITOR_TEST_CASES = [

	// 1️⃣ Simple View-only invoice
	{
		name: 'View only – no edit rights',
		invoiceDocument: {
			invoiceDocumentId: 1,
			invoiceDocumentStatusId: 40,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: false,
			showRepost: false,
			title: 'View Invoice'
		}
	},

	// 2️⃣ Editable invoice (opened from list)
	{
		name: 'Editable invoice – normal status',
		invoiceDocument: {
			invoiceDocumentId: 2,
			invoiceDocumentStatusId: 40,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'Y'
		},
		expected: {
			uiViewAttribute: 'EDITABLE',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: false,
			showPostToERP: false,
			showRepost: false,
			title: 'Invoice'
		}
	},

	// 3️⃣ Make Corrections case
	{
		name: 'Correction mode invoice',
		invoiceDocument: {
			invoiceDocumentId: 3,
			invoiceDocumentStatusId: 50,
			invoiceDocumentStatusAttribute: 'C',
			allowInvoiceEditing: 'Y'
		},
		expected: {
			uiViewAttribute: 'EDITABLE',
			uiStatusAttribute: 'CORRECTION',
			mode: 'MakeCorrections',
			readOnly: false,
			showPostToERP: false,
			showRepost: false,
			title: 'Make Corrections'
		}
	},

	// 4️⃣ Manual Entry invoice
	{
		name: 'Manual Entry invoice',
		invoiceDocument: {
			invoiceDocumentId: 4,
			invoiceDocumentStatusId: 50,
			invoiceDocumentStatusAttribute: 'M',
			allowInvoiceEditing: 'Y'
		},
		expected: {
			uiViewAttribute: 'EDITABLE',
			uiStatusAttribute: 'MANUAL',
			mode: 'ManualEntry',
			readOnly: false,
			showPostToERP: false,
			showRepost: false,
			title: 'Invoice'
		}
	},

	// 5️⃣ Approval – user has authority
	{
		name: 'Approval stage – approver',
		invoiceDocument: {
			invoiceDocumentId: 5,
			invoiceDocumentStatusId: 70,
			invoiceDocumentStatusAttribute: 'C',
			allowInvoiceEditing: 'N'
		},
		expected: {
			uiViewAttribute: 'APPROVAL',
			uiStatusAttribute: 'CORRECTION',
			mode: 'Approval',
			readOnly: true,
			showPostToERP: false,
			showRepost: false,
			title: 'Approve Invoice'
		}
	},

	// 6️⃣ Ready to Post
	{
		name: 'Ready to Post',
		invoiceDocument: {
			invoiceDocumentId: 6,
			invoiceDocumentStatusId: 170,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N',
			approvalLinkText: 'Post to ERP System'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: true,
			showRepost: false,
			title: 'View Invoice'
		}
	},

	// 7️⃣ Posted – Repost allowed
	{
		name: 'Posted – Repost allowed',
		invoiceDocument: {
			invoiceDocumentId: 7,
			invoiceDocumentStatusId: 180,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N',
			approvalLinkText: 'Repost to ERP System'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: false,
			showRepost: true,
			title: 'View Invoice'
		}
	},

	// 8️⃣ Archived invoice
	{
		name: 'Archived invoice',
		invoiceDocument: {
			invoiceDocumentId: 8,
			invoiceDocumentStatusId: 999,
			invoiceDocumentStatusAttribute: 'N',
			allowInvoiceEditing: 'N'
		},
		expected: {
			uiViewAttribute: 'READ_ONLY',
			uiStatusAttribute: 'NORMAL',
			mode: 'View',
			readOnly: true,
			showPostToERP: false,
			showRepost: false,
			title: 'View Invoice'
		}
	}
];




//export const isFieldReadOnly = (fieldName, props, invoiceIndicator, isEditable) => {

//	const isCreditMemoMode = props.info.mode == Constants.CREDIT_MEMO;
//	const enableForCreditMemo = isCreditMemoMode && invoiceIndicator === 2;
//	const systemCheck = () => {
//		switch (fieldName) {
//			case "Invoice Number":
//				return (
//					(!isCreditMemoMode && (state.readOnly || isInvoiceSource || !broadCastValidation.isInvoiceNumberEnabled)) || !enableForCreditMemo
//				);
//				break;
//			case "Invoice Date":

//				return (
//					(!isCreditMemoMode && ((!ignorePreviousCases && (state.readOnly || isInvoiceSource || !broadCastValidation.isInvoiceDateEnabled)) || (readOnlyForAdmin))) || !enableForCreditMemo
//				);

//			case "GL Date":

//				return (
//					(!isCreditMemoMode && ((props.info.mode !== Constants.EDIT_DATES && (state.readOnly || isInvoiceSource || !broadCastValidation.isGLDateEnabled))
//						|| (readOnlyForAdmin))) || !enableForCreditMemo);

//			case "Due Date":
//				return (


//				);
//			default:
//				return false; // No system restrictions by default
//		}
//	};

//	// If system check disables field, stop here
//	if (systemCheck()) {
//		return true;
//	}

//	// Else, apply group rule check
//	return !isEditable(fieldName);
//};



// -----------------------------
// UI-side editor truth engine
// -----------------------------
// Backend returns only concrete facts:
//  - isAdmin (server-truth)
//  - isDifferentCompany (logged-in company != invoice company)
//  - routingNumberOfApprovalLevels (max approval depth)
// Everything else (mode + abilities) is derived on the UI.

export const isMultipleOf20 = (n) => (Number(n) % 20 === 0);

// Backend stores auto-approved as "Y"/"N" (most common), but some DTOs may expose
// it as boolean/number depending on the source (list/report vs editor).
export const getIsAutoApproved = (invoiceDocument) => {
	const v =
		invoiceDocument?.isAutoApproved ??
		invoiceDocument?.IsAutoApproved ??
		invoiceDocument?.is_auto_approved ??
		invoiceDocument?.autoApproved ??
		invoiceDocument?.auto_approved;

	if (v === true) return true;
	if (v === 1 || v === "1") return true;
	if (typeof v === "string") {
		const s = v.trim().toUpperCase();
		return s === "Y" || s === "TRUE";
	}
	return false;
};

export const resolveWorkflowMode = (invoiceDocument, routingNumberOfApprovalLevels = 0, isDifferentCompany = false) => {
	if (!invoiceDocument) return Constants.VIEW;

	// Company mismatch is represented as VIEW on UI (with a separate flag to show change-company UI).
	if (isDifferentCompany) return Constants.VIEW;

	// Business rule: ModelDef 3 invoices coming from source (invoiceSource === 1) are always view-only.
	if (Number(invoiceDocument?.modelDefId) === 3 && Number(invoiceDocument?.invoiceSource) === 1) {
		return Constants.VIEW;
	}

	// Structural
	if ((invoiceDocument.applyToInvoiceDocumentId || 0) > 0) return Constants.CREDIT_MEMO;

	// User entry
	if (invoiceDocument.invoiceDocumentStatusAttribute === "U") return Constants.USER_ENTRY;

	// Status-driven workflow
	const statusId = Number(invoiceDocument.invoiceDocumentStatusId || 0);
	const maxApprovalLevelBase = (Number(routingNumberOfApprovalLevels || 0) * 20) + 50;
	const approvalUpperBound = maxApprovalLevelBase + (10 * Number(isMultipleOf20(statusId)));

	if (statusId < 40) return Constants.VIEW;
	if (statusId === 40 || statusId === 60 || statusId === 180) return Constants.MAKE_CORRECTIONS;
	if (statusId === 170) return Constants.VIEW;

	if (
		statusId >= 50 &&
		statusId !== 60 &&
		statusId !== maxApprovalLevelBase &&
		statusId <= approvalUpperBound
	) {
		return Constants.APPROVAL;
	}

	return Constants.VIEW;
};


export const isApprovalLevelBreached = ({
	routingNumberOfApprovalLevels = 0,
	invoiceDocumentStatusId = 0
}) => {
	const noramlizedStatus = (invoiceDocumentStatusId) % 20 === 0 ? invoiceDocumentStatusId - 30 : invoiceDocumentStatusId;
	const maxStatus = (routingNumberOfApprovalLevels * 20) + 50;
	return maxStatus <= noramlizedStatus;
}

export const resolveWorkflowCapabilities = (
	invoiceDocument,
	{
		isAdmin = false,
		routingNumberOfApprovalLevels = 0,
		isDifferentCompany = false,
	} = {}
) => {
	const noramlizedStatus = (invoiceDocument?.invoiceDocumentStatusId) % 20 === 0 ? invoiceDocument?.invoiceDocumentStatusId - 30 : invoiceDocument?.invoiceDocumentStatusId;
	const statusId = Number(invoiceDocument?.invoiceDocumentStatusId || 0);
	const eligibleForOperation = !isDifferentCompany;

	const routingThreshold =
		(Number(routingNumberOfApprovalLevels || 0) * 20) +
		(50);

	const isAutoApproved = getIsAutoApproved(invoiceDocument);
	const hasPassedApproval = noramlizedStatus >= routingThreshold;
	const isNotPosted = statusId < 170;

	const overrideMode =
		isAdmin &&
		isAutoApproved &&
		hasPassedApproval &&
		isNotPosted &&
		eligibleForOperation;

	const canOverride =
		isAdmin &&
		isAutoApproved &&
		hasPassedApproval &&
		isNotPosted &&
		eligibleForOperation;

	const canEditDates =
		isAdmin &&
		hasPassedApproval &&
		isNotPosted &&
		!overrideMode &&
		eligibleForOperation;

	const canDelete = !hasPassedApproval &&
		statusId !== 170 &&
		statusId !== 180
	const canPostCreditMemo = invoiceDocument?.invoiceIndicator == 2 &&
		(statusId !== 170) &&
		hasPassedApproval;

	return {
		canPostToERP:
			hasPassedApproval &&
			statusId !== 170 &&
			statusId !== 180 &&
			eligibleForOperation,
		canRepostToERP: statusId === 180 && eligibleForOperation,
		canOverride,
		canEditDates,
		hasDifferentCompany: !eligibleForOperation,
		canDelete: canDelete,
		canPostCreditMemo: canPostCreditMemo
	};



};
// ---------------------------------------------
// Repost / Partial-post eligibility (UI-derived)
// ---------------------------------------------
// List already derives these values from row data. We mirror the same derivation here
// using the concrete facts available on invoiceDocument so editor and list behave the same.

export const isRepostRuleEditable = (userGroupRules = [], modelDefId) => {
	const md = Number(modelDefId || 0);
	return !!(userGroupRules || []).find(r =>
		r?.label === 'Repost Invoice' &&
		Number(r?.Model_def_id) === md &&
		String(r?.editable) === '1'
	);
};
export const isCreditMemoPostEnabled = (userGroupRules = [], modelDefId) => {
	const md = Number(modelDefId || 0);
	return !!(userGroupRules || []).find(r =>
		r?.label === 'Post Credit Memo' &&
		Number(r?.Model_def_id) === md &&
		String(r?.editable) === '1'
	);
};

export const derivePartialInvoiceStatus = (invoiceDocument, repostRuleEditable) => {
	const postStatus = (invoiceDocument?.invoicePostStatus || '').toString().toLowerCase();
	const isEligible = postStatus === 'partial posted' || postStatus === 'fully posted';

	const total = Number(invoiceDocument?.totalNumberInvoice || 0);
	const posted = Number(invoiceDocument?.postedNumberInvoice || 0);

	if (!isEligible || total <= 0) return null;

	// Fully posted -> view details (never a partial repost)
	if (posted >= total) {
		return { dataMode: 'ViewDetails', shouldDisplay: true, canRepost: false };
	}

	// Partially posted
	if (posted < total) {
		if (repostRuleEditable) {
			return { dataMode: 'PartialPost', shouldDisplay: true, canRepost: true };
		}
		// Rule not active -> still allow viewing details, but no repost
		return { dataMode: 'ViewDetails', shouldDisplay: true, canRepost: false };
	}

	return null;
};

export const getInvalidIoPoRowsForServiceTerm = (invoiceDocument) => {

	if (!invoiceDocument) return [];

	const {
		modelDefId,
		serviceDateFilterForIoLookup,
		serviceTermStartDateString,
		serviceTermEndDateString,
		invoiceDocumentEditorDetails
	} = invoiceDocument;

	if (modelDefId !== 1 || serviceDateFilterForIoLookup !== "MANDATORY")
		return [];

	if (!serviceTermStartDateString || !serviceTermEndDateString)
		return [];

	const invoiceStart = new Date(serviceTermStartDateString);
	const invoiceEnd = new Date(serviceTermEndDateString);

	invoiceStart.setHours(0, 0, 0, 0);
	invoiceEnd.setHours(0, 0, 0, 0);

	return (invoiceDocumentEditorDetails || []).filter(line => {

		if (!line.poCode || line.poCode.trim() === "" || !line.ioNumber || line.ioNumber.trim() === "") {
			return false;
		}

		if (!line.startDate || !line.endDate)
			return false;

		const ioStart = new Date(line.startDate);
		const ioEnd = new Date(line.endDate);

		ioStart.setHours(0, 0, 0, 0);
		ioEnd.setHours(0, 0, 0, 0);

		const hasOverlap =
			ioStart <= invoiceEnd &&
			ioEnd >= invoiceStart;

		return !hasOverlap;
	});
};